/*
 * Brand
 *
 * Resolves a dealership's own colors into the tokens the marketing surface
 * draws with. Every replicated website belongs to a different company, so there
 * is no fixed brand color here the way there is in a single-brand storefront —
 * the palette arrives at runtime with the dealership preferences.
 *
 * The two colors are used EXACTLY as authored. Nothing here deepens, lightens
 * or shifts a brand color so that text can sit on it. An earlier version did,
 * and on a cream primary (#EAE4D5) that correction drained it to #78756D — a
 * color the dealership never chose and would not recognise. A brand color
 * that is awkward to put text on is a fact about the brand, not a defect to be
 * edited out.
 *
 * What this file still does is CHOOSE, never alter. Given a ground it picks
 * whichever authored color reads on it, falling back to near-black or white
 * only when neither will. Choosing a legible foreground is not modifying the
 * palette; it is the reason a palette has two colors.
 *
 * The colors keep their own jobs. `primary` is the brand: filled surfaces,
 * buttons, marks. `secondary` is its partner, used where the primary cannot go
 * — most often as text on the white page, and as the header band.
 */

// Used until the dealership preferences resolve, and for any dealership whose
// record is missing or malformed. The blue is the storefront's own accent.
const FALLBACK = {
    primary: '#0071E3',
    secondary: '#1D1D1F'
};

// The marketing surface is mostly a white-ground design, but two sections — the
// stakes band and the walkthrough call to action — sit on near-black. Which of
// the two brand colors reads is a different answer on each, so each ground is
// resolved separately.
const GROUND = [255, 255, 255];
const DARK_GROUND = [29, 29, 31];   // Theme.colors.dark()
const DARK_INK = [29, 29, 31];      // #1D1D1F
const LIGHT_INK = [255, 255, 255];

// WCAG AA for body text, and the lower bar for a UI component's boundary.
const MIN_CONTRAST = 4.5;
const UI_CONTRAST = 3;

const parseHex = value => {

    let hex = String(value || '').trim().replace('#', '');
    if(hex.length === 3) {
        hex = hex.split('').map(char => `${char}${char}`).join('');
    }
    if(hex.length !== 6 || /[^0-9a-f]/i.test(hex) === true) {
        return null;
    }
    return [0, 2, 4].map(offset => parseInt(hex.slice(offset, offset + 2), 16));
}

const toHex = channels => {
    return `#${channels.map(channel => {
        let value = Math.round(Math.min(255, Math.max(0, channel)));
        return value.toString(16).padStart(2, '0');
    }).join('')}`.toUpperCase();
}

// WCAG 2.1 relative luminance.
const luminance = channels => {
    let values = channels.map(channel => {
        let value = channel / 255;
        return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
    });
    return (0.2126 * values[0]) + (0.7152 * values[1]) + (0.0722 * values[2]);
}

const contrast = (a, b) => {
    let high = Math.max(luminance(a), luminance(b));
    let low = Math.min(luminance(a), luminance(b));
    return (high + 0.05) / (low + 0.05);
}

const mix = (channels, target, amount) => {
    return channels.map((channel, index) => channel + ((target[index] - channel) * amount));
}

// The first authored color that reads on this ground, or the neutral that does
// when neither will. Nothing is altered here — a color either clears the bar on
// this ground or it is passed over in favour of the other one.
const pick = (candidates, ground, threshold = MIN_CONTRAST) => {
    let match = candidates.find(channels => contrast(channels, ground) >= threshold);
    return match || (luminance(ground) > 0.5 ? DARK_INK : LIGHT_INK);
}

// Hover moves away from the page ground in whichever direction is visible: a
// dark brand lightens, a light brand deepens. This is an interaction state, not
// the brand color — the resting color it moves away from stays as authored.
const shift = channels => {
    return luminance(channels) > 0.5 ? mix(channels, [0, 0, 0], 0.12) : mix(channels, [255, 255, 255], 0.12);
}

// Whichever of near-black or white reads better on the supplied color.
const foreground = channels => {
    return contrast(channels, DARK_INK) >= contrast(channels, LIGHT_INK) ? toHex(DARK_INK) : toHex(LIGHT_INK);
}

const resolve = (colors = {}) => {

    let primary = parseHex(colors.primary) || parseHex(FALLBACK.primary);
    let secondary = parseHex(colors.secondary) || parseHex(FALLBACK.secondary);

    // Filled surfaces are the primary, exactly as authored. Only the lettering
    // on top is decided here — near-black on a cream, white on a navy — which
    // is how one rule serves every dealership without touching anyone's color.
    let onPrimary = foreground(primary);

    // Accent text and icons on the white page. A cream cannot go there at
    // 1.27:1 so the secondary takes it; a navy primary reads and keeps it.
    let ink = pick([primary, secondary], GROUND);

    // The near-black bands invert that. A cream reads instantly on #1D1D1F
    // where a charcoal secondary would disappear, so the same two colors are
    // offered against the other ground and the answer usually flips.
    let inkOnDark = pick([primary, secondary], DARK_GROUND);

    // A filled control on those bands only has to separate from the band, which
    // is the lower UI bar rather than the text bar.
    let fillOnDark = pick([primary, secondary], DARK_GROUND, UI_CONTRAST);

    return {
        fill: toHex(primary),
        fillOnDark: toHex(fillOnDark),
        ink: toHex(ink),
        inkOnDark: toHex(inkOnDark),
        onFill: onPrimary,
        onInkOnDark: foreground(inkOnDark),
        onPrimary: onPrimary,
        onSurface: foreground(secondary),
        primary: toHex(primary),
        primaryHover: toHex(shift(primary)),
        secondary: toHex(secondary),
        surface: toHex(secondary),
        surfaceHover: toHex(shift(secondary))
    };
}

// Resolved once the dealership preferences land. Held at module scope rather
// than in context so that Theme.js — which is a plain object of functions, not
// a component — can read it from any call site without threading props.
let brand = null;

const Brand = {

    set: colors => {

        brand = resolve(colors);

        // Hand the palette to the stylesheet as well. A few pieces of the
        // marketing surface are painted by CSS rather than inline styles (the
        // quote badge, text selection), and they have to match.
        let root = document.documentElement;
        root.style.setProperty('--brand', brand.fill);
        root.style.setProperty('--brand-ink', brand.ink);
        root.style.setProperty('--brand-on', brand.onFill);
        root.style.setProperty('--brand-raw', brand.primary);
        root.style.setProperty('--brand-secondary', brand.secondary);
        root.style.setProperty('--brand-surface', brand.surface);
        root.style.setProperty('--brand-surface-on', brand.onSurface);

        return brand;
    },

    // Falling back rather than returning null keeps every call site total —
    // components render correctly before preflight completes.
    get: () => brand || resolve({}),

    // Whichever authored color reads as text on the white page.
    ink: () => Brand.get().ink,

    // The same question asked against the near-black bands instead.
    inkOnDark: () => Brand.get().inkOnDark,
    onInkOnDark: () => Brand.get().onInkOnDark,

    // Filled controls: the dealership's primary exactly as authored, with a
    // foreground chosen to read on it.
    fill: () => Brand.get().fill,
    onFill: () => Brand.get().onFill,

    // The same, for filled controls sitting on the near-black bands.
    fillOnDark: () => Brand.get().fillOnDark,

    // The dealership's colors, as authored.
    primary: () => Brand.get().primary,
    primaryHover: () => Brand.get().primaryHover,
    onPrimary: () => Brand.get().onPrimary,
    secondary: () => Brand.get().secondary,

    // The secondary in its role as a ground.
    surface: () => Brand.get().surface,
    surfaceHover: () => Brand.get().surfaceHover,
    onSurface: () => Brand.get().onSurface,

    // Tint of a color, for chips, wash backgrounds, and focus rings. Defaults
    // to whichever color is currently carrying the accent.
    tint: (alpha = 0.08, color) => {
        let channels = parseHex(color || Brand.get().ink) || [0, 0, 0];
        return `rgba(${channels.join(',')},${alpha})`;
    },

    // Exposed for callers that need the maths on an arbitrary color, such as
    // choosing a foreground for a dealership logo backdrop.
    utils: {
        contrast: (a, b) => {
            let first = parseHex(a);
            let second = parseHex(b);
            return first && second ? contrast(first, second) : 0;
        },
        foreground: color => {
            let channels = parseHex(color);
            return channels ? foreground(channels) : toHex(DARK_INK);
        },
        isPale: color => {
            let channels = parseHex(color);
            return channels ? luminance(channels) > 0.75 : false;
        }
    }
}

export default Brand;
